import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number | undefined} */
  #pendingTimer;

  /** @type {number | undefined} */
  #fallbackOpenTimer;

  /** @type {boolean} */
  #waitingForCartRender = false;

  /** @type {number} */
  #itemCountBeforeAdd = 0;

  /** @type {number | undefined} */
  #minimumItemCountAfterAdd;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartAddPending, this.#handleCartAddPending);
    document.addEventListener(ThemeEvents.cartAddComplete, this.#handleCartAddComplete);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete);
    document.addEventListener(ThemeEvents.cartRenderComplete, this.#handleCartRenderComplete);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartAddPending, this.#handleCartAddPending);
    document.removeEventListener(ThemeEvents.cartAddComplete, this.#handleCartAddComplete);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartAddComplete);
    document.removeEventListener(ThemeEvents.cartRenderComplete, this.#handleCartRenderComplete);
    this.#clearPendingTimer();
    this.#clearFallbackOpenTimer();
  }

  #handleCartAddPending = async (event) => {
    if (!this.#shouldHandleProductFormEvent(event)) return;

    this.#waitingForCartRender = true;
    this.#itemCountBeforeAdd = this.#cartItemCount;
    this.#minimumItemCountAfterAdd = undefined;
    await this.#closeBlockingDialog();
    if (!this.#waitingForCartRender || this.refs.dialog.open) return;
    this.#showPendingIndicator(this.#isMobile ? 0 : 650);
  };

  #handleCartAddComplete = () => {
    if (!this.#waitingForCartRender) return;

    this.#waitingForCartRender = false;
    this.#minimumItemCountAfterAdd = undefined;
    this.#hidePendingIndicator();
    this.#clearFallbackOpenTimer();
  };

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open') || !this.#shouldHandleProductFormEvent(event)) return;
    if (event.detail.data?.didError) return;

    this.#waitingForCartRender = true;
    this.#minimumItemCountAfterAdd = this.#itemCountBeforeAdd + (Number(event.detail.data?.itemCount) || 1);
    this.#showPendingIndicator(250);
    this.#openWhenCartHasItems();

    this.#clearFallbackOpenTimer();
    this.#fallbackOpenTimer = setTimeout(() => {
      this.#openWhenCartHasItems();
      if (!this.refs.dialog.open) this.#hidePendingIndicator();
    }, 4000);
  };

  #handleCartRenderComplete = (event) => {
    if (!this.#waitingForCartRender) return;
    if (event.detail?.sectionId !== this.#cartItemsSectionId) return;

    this.#openWhenCartHasItems();
  };

  async #openWhenCartHasItems() {
    if (!this.#cartHasAddedItems()) return;

    await this.#closeBlockingDialog();

    this.#waitingForCartRender = false;
    this.#minimumItemCountAfterAdd = undefined;
    this.#hidePendingIndicator();
    this.#clearFallbackOpenTimer();
    this.open();
  }

  /**
   * @param {Event} event
   */
  #shouldHandleProductFormEvent(event) {
    return 'detail' in event && event.detail?.data?.source === 'product-form-component';
  }

  #cartHasAddedItems() {
    if (this.#minimumItemCountAfterAdd) {
      return this.#cartItemCount >= this.#minimumItemCountAfterAdd;
    }

    return Boolean(this.querySelector('.cart-form'));
  }

  get #cartItemCount() {
    const itemCount = this.querySelector('[ref="cartItemCount"]')?.textContent;

    return itemCount ? Number(itemCount.trim()) || 0 : 0;
  }

  get #cartItemsSectionId() {
    return this.querySelector('cart-items-component')?.getAttribute('data-section-id');
  }

  get #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  async #closeBlockingDialog() {
    const quickAddDialog = document.getElementById('quick-add-dialog');
    const openDialog = quickAddDialog?.querySelector('dialog[open]');

    if (!quickAddDialog || !openDialog) return;

    await new Promise((resolve) => {
      let resolved = false;
      const done = () => {
        if (resolved) return;

        resolved = true;
        quickAddDialog.removeEventListener(DialogCloseEvent.eventName, done);
        resolve(undefined);
      };

      quickAddDialog.addEventListener(DialogCloseEvent.eventName, done, { once: true });

      if (typeof quickAddDialog.closeDialog === 'function') {
        quickAddDialog.closeDialog();
      } else {
        openDialog.close();
        done();
      }

      setTimeout(done, 700);
    });
  }

  #showPendingIndicator(delay = 0) {
    this.#clearPendingTimer();

    this.#pendingTimer = setTimeout(() => {
      this.refs.pendingIndicator?.removeAttribute('hidden');
    }, delay);
  }

  #hidePendingIndicator() {
    this.#clearPendingTimer();
    this.refs.pendingIndicator?.setAttribute('hidden', '');
  }

  #clearPendingTimer() {
    if (!this.#pendingTimer) return;

    clearTimeout(this.#pendingTimer);
    this.#pendingTimer = undefined;
  }

  #clearFallbackOpenTimer() {
    if (!this.#fallbackOpenTimer) return;

    clearTimeout(this.#fallbackOpenTimer);
    this.#fallbackOpenTimer = undefined;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
